import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalShell } from "@/components/PortalShell";

export default function AdminPartnerAcquisition() {
  const [dash, setDash] = useState(null);
  const [dashError, setDashError] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => {
    api.get("/admin/partner-acquisition/dashboard").then(({ data }) => { setDash(data); setDashError(false); }).catch(() => setDashError(true));
    api.get("/admin/prospect-campaigns").then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  };
  useEffect(load, []);

  const createCampaign = async () => {
    try {
      await api.post("/admin/prospect-campaigns", { name });
      toast.success("Campaign created (Draft)"); setName(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doPreview = async (c) => {
    const { data } = await api.get(`/admin/prospect-campaigns/${c.id}/preview`);
    setPreview({ id: c.id, ...data });
  };
  const markReady = async (c) => {
    await api.put(`/admin/prospect-campaigns/${c.id}/status?status=ready`); load();
  };
  const send = async (c) => {
    if (!window.confirm(`Send campaign "${c.name}" to eligible prospects?`)) return;
    try {
      const { data } = await api.post(`/admin/prospect-campaigns/${c.id}/send`, { confirm: true });
      toast.success(`Sent ${data.sent} — excluded ${data.excluded}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <PortalShell title="Partner Acquisition" subtitle="Prospect funnel, conversion attribution and outreach campaigns.">
    <div className="space-y-6" data-testid="admin-acquisition-page">
      {!dash && !dashError && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="acquisition-loading">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      )}
      {dashError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="acquisition-error">
          Couldn't load the acquisition dashboard. <button className="underline font-semibold" onClick={load}>Retry</button>
        </div>
      )}
      {dash && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="acquisition-cards">
            {Object.entries(dash.cards).map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.replaceAll("_", " ")}</div>
                <div className="text-xl font-black text-slate-900">{v}</div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="acquisition-funnel">
            <h2 className="text-sm font-bold text-slate-700 mb-2">Conversion funnel</h2>
            <div className="flex flex-wrap gap-2">
              {dash.funnel.map((f) => (
                <div key={f.stage} className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-semibold">{f.stage}</span>: {f.n}
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{dash.note}</p>
          </div>
        </>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <h2 className="text-sm font-bold text-slate-700">Prospect Campaigns</h2>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name"
            data-testid="campaign-name-input" className="max-w-xs" />
          <Button onClick={createCampaign} disabled={!name.trim()} data-testid="campaign-create-btn">Create Draft</Button>
        </div>
        <div className="space-y-2">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3 text-sm"
              data-testid={`campaign-row-${c.id}`}>
              <span className="font-semibold">{c.name}</span>
              <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100">{c.status}</span>
              {c.counts?.sent != null && <span className="text-xs text-slate-500">sent {c.counts.sent} / excluded {c.counts.excluded}</span>}
              <span className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => doPreview(c)} data-testid={`campaign-preview-${c.id}`}>Preview</Button>
                {c.status === "draft" && <Button size="sm" variant="outline" onClick={() => markReady(c)} data-testid={`campaign-ready-${c.id}`}>Mark Ready</Button>}
                {c.status === "ready" && <Button size="sm" onClick={() => send(c)} data-testid={`campaign-send-${c.id}`}>Send</Button>}
              </span>
            </div>
          ))}
          {campaigns.length === 0 && <div className="text-sm text-slate-500">No campaigns yet.</div>}
        </div>
        {preview && (
          <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-sm" data-testid="campaign-preview-box">
            Selected {preview.selected} · Eligible {preview.eligible} · Excluded {preview.excluded}
            {Object.keys(preview.excluded_reasons || {}).length > 0 && (
              <span className="text-xs text-slate-600"> — {Object.entries(preview.excluded_reasons).map(([r, n]) => `${r}: ${n}`).join(", ")}</span>
            )}
          </div>
        )}
      </div>
    </div>
    </PortalShell>
  );
}
